import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert } from "antd";
import { CustomButton, PreviousButton } from "@comps/components";
import { getBlockingSetupItems, SETUP_STATUS } from "../readiness";
import { StatusTag } from "./ReadinessSummary";

// Final step — recap required setup and optional modules. Finish clears the
// wizard draft and drops the admin into the dashboard.
const Step6GoLive = (props) => {
  const { wizard, onBack, goLive, readiness, statuses } = props;
  const navigate = useNavigate();
  const blockers = getBlockingSetupItems(readiness, wizard);

  const org = wizard.org?.counts || {};
  const orgTotal = ["locations", "areas", "branches", "departments", "grades", "designations", "jobTitles"]
    .reduce((sum, k) => sum + (org[k] || 0), 0);

  const emp = wizard.employees || {};
  const machines = wizard.machines || {};
  const flows = wizard.requestflow?.configured || [];

  const machineLabelMap = { adms: "ADMS enabled", standard: "standard machines" };
  const machineLabel = machineLabelMap[machines.category] || null;

  const checklist = [
    {
      key: "company",
      text: `Organisation - ${readiness.company.record?.name || wizard.company?.name || "company"} - ${orgTotal || Object.values(readiness.org.counts || {}).reduce((s, n) => s + (n || 0), 0)} structure items`,
    },
    {
      key: "employees",
      text: `Employees - ${readiness.employees.count || emp.count || 0} active employee${(readiness.employees.count || emp.count || 0) === 1 ? "" : "s"}`,
    },
    {
      key: "shifts",
      text: `Shifts & attendance - ${readiness.shifts.timeSlotCount || 0} time slots, ${readiness.shifts.assignedCount || 0} assigned`,
    },
    {
      key: "machines",
      text: machineLabel ? `Attendance devices - ${machineLabel}` : `Attendance devices - ${readiness.machines.total || 0} connected`,
    },
    {
      key: "leave",
      text: `Leave setup - ${readiness.leave.count || 0} leave type${(readiness.leave.count || 0) === 1 ? "" : "s"}, ${readiness.leave.quotaCount || 0} quota record${(readiness.leave.quotaCount || 0) === 1 ? "" : "s"}`,
    },
    {
      key: "requestflow",
      text: `Approval flows - ${readiness.requestflow.configuredCount || flows.length} request type${(readiness.requestflow.configuredCount || flows.length) === 1 ? "" : "s"} configured`,
    },
    {
      key: "loans",
      text: `Loans & advances - ${readiness.loans.categoryCount || 0} categories, ${readiness.loans.typeCount || 0} types`,
    },
    {
      key: "payrollpolicies",
      text: `Payroll setup - settings ${readiness.payroll.configured ? "saved" : "not saved"}, ${readiness.payroll.payrollCount || 0} payrolls`,
    },
  ];

  const countByStatus = (status) => Object.values(statuses).filter((item) => item === status).length;
  const canFinish = blockers.length === 0;

  return (
    <>
      <div className="onb-card-title">Review & finish setup</div>
      <div className="onb-card-sub">
        Review required setup and optional modules. You can finish when company setup and employees are ready.
      </div>

      {blockers.length ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Required setup still needs attention"
          description={blockers.join(" ")}
        />
      ) : (
        <Alert
          style={{ marginBottom: 16 }}
          type="success"
          showIcon
          message="Required setup is complete"
          description="Optional modules can be refined later from their own menus."
        />
      )}

      <div className="onb-summary-grid">
        <div className="onb-sum"><div className="n">{countByStatus(SETUP_STATUS.complete)}</div><div className="l">Complete</div></div>
        <div className="onb-sum"><div className="n">{countByStatus(SETUP_STATUS.skipped)}</div><div className="l">Skipped</div></div>
        <div className="onb-sum"><div className="n">{countByStatus(SETUP_STATUS.optional)}</div><div className="l">Optional pending</div></div>
      </div>

      <div className="onb-section-h">Setup summary</div>
      <div className="onb-panel" style={{ padding: "6px 16px" }}>
        {checklist.map((c, i) => (
          <div key={i} className="onb-check-row">
            <StatusTag status={statuses[c.key]} />
            <span>{c.text}</span>
          </div>
        ))}
      </div>

      <div className="onb-note" style={{ marginTop: 18 }}>
        Fine-tune anything later: manage people in{" "}
        <b style={{ cursor: "pointer" }} onClick={() => navigate("/employees/company")}>People</b>,
        run payroll from the{" "}
        <b style={{ cursor: "pointer" }} onClick={() => navigate("/payrollmanagement/payrolls")}>Payroll module</b>,
        and add HR admins or tweak approval flows in{" "}
        <b style={{ cursor: "pointer" }} onClick={() => navigate("/masters/system")}>Admin → System</b>.
      </div>

      <div className="onb-foot">
        <PreviousButton onClick={onBack} text="Back" />
        <span className="spacer" />
        <CustomButton type="primary" disabled={!canFinish} onClick={goLive} text="Finish Setup" />
      </div>
    </>
  );
};

export default Step6GoLive;
